import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import {lighten} from '@mui/system/colorManipulator';
import {DataGrid, type GridColDef} from '@mui/x-data-grid';
import {enUS, esES} from '@mui/x-data-grid/locales';
import type {FC} from 'react';
import {useTranslation} from 'react-i18next';
import Title from '@/components/common/Title';
import type {InventoryStem} from '@/domain/entities/InventoryStem';
import {primaryColor} from '@/theme/theme';

//TYPES
type Props = {
  inventoryStems: Array<InventoryStem>;
};

const InventoryStemTable: FC<Props> = ({inventoryStems}) => {
  const {t, i18n} = useTranslation();
  const lang = i18n.language;

  const baseLocale =
    lang === 'es'
      ? esES.components.MuiDataGrid.defaultProps.localeText
      : enUS.components.MuiDataGrid.defaultProps.localeText;

  const columns = [
    createColumn('id', '#', {flex: 0}),
    createColumn('diameter', t('inventoryPlots.stemDiameter').toUpperCase(), {flex: 1}),
    createColumn('height', t('inventoryPlots.height').toUpperCase()),
    createColumn('volumeUsc', t('inventoryPlots.volumeUsc').toUpperCase()),
    createColumn('volumeIfn', t('inventoryPlots.volumeIfn').toUpperCase()),
  ];

  const inventoryStemsForTable = inventoryStems?.map((stem, index) => ({
    id: index + 1,
    diameter: stem.diameterInMm,
    height: stem.height,
    volumeUsc: stem.volumeUsc.toLocaleString('es-ES', {
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
    }),
    volumeIfn: stem.volumeIfn.toLocaleString('es-ES', {
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
    }),
  }));

  return (
    <>
      <Stack direction="row" sx={{justifyContent: 'space-between', alignItems: 'center'}}>
        <Title size="h6" label={t('inventoryPlots.stems')} />
        <Typography
          variant="body2"
          sx={{color: 'grey.700'}}
        >{`${inventoryStems.length} ${t('inventoryPlots.records')}`}</Typography>
      </Stack>
      <Stack
        sx={{
          width: '100%',
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
          display: 'flex',
        }}
      >
        <DataGrid
          rows={inventoryStemsForTable}
          columns={columns}
          disableRowSelectionOnClick
          disableColumnSelector
          disableColumnMenu
          disableColumnFilter
          rowHeight={30}
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: 10,
                page: 0,
              },
            },
          }}
          pageSizeOptions={[10]}
          localeText={{
            ...baseLocale,
            noRowsLabel: t('inventoryPlots.empty'),
          }}
          sx={styles.table}
        />
      </Stack>
    </>
  );
};
export default InventoryStemTable;

const createColumn = (field: string, label: string, extraProps?: Partial<GridColDef>) => ({
  field: field,
  headerName: label,
  flex: extraProps?.flex !== undefined ? extraProps?.flex : 1,
  ...extraProps,
});

const styles = {
  table: {
    minHeight: 0,
    width: '100%',
    boxShadow: 'none',
    borderRadius: '0px',
    height: '100%',
    '& .MuiDataGrid-cell': {
      display: 'flex',
      alignItems: 'center',
    },
    '& .MuiPaper-root': {
      boxShadow: 'none !important',
    },
    '--Paper-shadow': 'none',
    '& .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeaderTitle': {
      color: 'common.white',
      fontWeight: 900,
      backgroundColor: lighten(primaryColor, 0.5),
    },
    '& .MuiDataGrid-cell:focus': {outline: 'none'},
    // Iconos de ordenar
    '& .MuiDataGrid-columnHeader--sortable .MuiSvgIcon-root': {
      color: 'common.white', // color de los iconos de ordenar
    },
    // Iconos de menú/filtro
    '& .MuiDataGrid-columnHeader:hover .MuiIconButton-root': {
      backgroundColor: 'primary.main',
      color: 'common.white',
    },
    // Icono de ordenar cuando está activo (sin hover)
    '& .MuiDataGrid-columnHeader--sorted .MuiButtonBase-root': {
      backgroundColor: 'primary.main',
    },
  },
};
